/** audio module entry (owner: audio-fx-builder). */
import type Phaser from 'phaser';
import type { IAudioDirector } from '@/core/types/contracts';
import type { GameContext } from '@/core/context';
import { SHOWCASES } from '@/content/ShowcaseScene';
import { AudioDirector, type AudioDirectorOptions } from './AudioDirector';
import { audioShowcase } from './showcase';

export { AudioDirector } from './AudioDirector';
export type { AudioDirectorOptions, AudioDirectorState } from './AudioDirector';
export { AudioKeys, AmbiencePresets, AUDIO_ALIASES, AMBIENCE_ALIASES, resolveAudioKey, footstepKeyFor, expandAmbience, pickFile, fetchAudioManifest } from './manifest';
export type { AudioEntry, AudioManifest, AudioKind } from './manifest';
export { spatialize, DEFAULT_SPATIAL } from './spatial';
export { Fader, crossfadeGains, duckGain } from './fades';

let director: IAudioDirector | null = null;
const SILENT: IAudioDirector = { playMusic() {}, setAmbience() {}, sfx() {}, duck() {}, async unlock() {} };

export function setAudioDirector(d: IAudioDirector): void { director = d; }
/** The active director (a silent stub until `ensureAudioDirector`/`setAudioDirector` runs). */
export function audio(): IAudioDirector { return director ?? SILENT; }

/**
 * Create (once) and register the real AudioDirector for this game. Safe to call from any scene;
 * the integrator is asked to call it from main.ts right after the GameContext exists.
 */
export function ensureAudioDirector(game: Phaser.Game, opts: AudioDirectorOptions = {}): AudioDirector {
  if (director instanceof AudioDirector && director.game === game) return director;
  const ctx = game.registry.get('ctx') as GameContext | undefined;
  const d = new AudioDirector(game, { settings: ctx?.settings, seed: ctx?.seed, ...opts });
  setAudioDirector(d);
  return d;
}

/**
 * Integrator hook (src/core/wiring.ts): registers `?showcase=audio` and, as soon as
 * `window.__nantian.game` exists (main.ts installs it right after the GameContext), creates the real
 * AudioDirector so UI/world/cinematic `audio()` calls are live without a main.ts change.
 */
export function registerShowcase(): void {
  SHOWCASES.audio = audioShowcase;
  if (typeof window === 'undefined') return;
  let tries = 0;
  const tryInstall = () => {
    if (director instanceof AudioDirector) return;
    const game = window.__nantian?.game;
    if (game) { try { ensureAudioDirector(game); } catch (e) { console.warn('[audio] director install failed', e); } return; }
    if (++tries < 600) setTimeout(tryInstall, 50);
  };
  tryInstall();
}
